import { useCart } from "@/hooks/cart/cart"
import { useAppDispatch, useAppSelector } from "@/hooks/redux"
import {
  setPrintEnabled,
  setPrintIsActive,
  setPrintIsLoading,
  setPrintLastLink,
  setPrintProducts,
} from "@/store/reducers/cartSlice"
import { createContext, FC, useCallback, useEffect, useMemo } from "react"
import {
  OnCompleteFnType,
  UpdateStateFnType,
  UsePrintCartReturnType,
} from "../types"

export const PrintContext = createContext<UsePrintCartReturnType | null>(null)

const Provider: FC = ({ children }) => {
  const dispatch = useAppDispatch()

  const {
    specification,
    products: productsCart,
    samples: samplesCart,
  } = useCart()

  const {
    products: products,
    samples: samples,
    enabled,
    isActive,
    isLoading,
    lastLink,
  } = useAppSelector(({ cart }) => cart.print)

  // старт показа лоадера происходит при клике на кнопку "Напечатать"
  // тк обход cors с кэшированием изображений на стороне next
  // тормозит основной поток
  // и юзер не увидит ничего, пока не завершится кэширование
  const _updateState: UpdateStateFnType = useCallback(
    (val, link = null) => {
      dispatch(setPrintIsLoading(val))
      dispatch(setPrintLastLink(link))
      dispatch(setPrintIsActive(val))
    },
    [dispatch],
  )

  const runPrint = useCallback(() => {
    _updateState(true)
    dispatch(setPrintEnabled(true))
  }, [_updateState, dispatch])

  const resetPrint = useCallback(() => {
    dispatch(setPrintEnabled(false))
    dispatch(setPrintProducts(null))
    _updateState(false)
  }, [_updateState, dispatch])

  const onComplete: OnCompleteFnType = useCallback(
    ({ url }) => {
      _updateState(false, url)
    },
    [_updateState],
  )

  useEffect(() => {
    if (!enabled || lastLink !== null) {
      return
    }
    dispatch(
      setPrintProducts({
        specification,
        products: productsCart,
        samples: samplesCart,
      }),
    )
  }, [dispatch, specification, enabled, lastLink, productsCart, samplesCart])

  const contextValue: UsePrintCartReturnType = useMemo(
    () => ({
      enabled,
      runPrint,
      resetPrint,
      onComplete,
      products,
      samples,
      isLoading,
      isActive,
    }),
    [
      enabled,
      onComplete,
      products,
      resetPrint,
      runPrint,
      samples,
      isLoading,
      isActive,
    ],
  )

  return (
    <PrintContext.Provider value={contextValue}>
      {children}
    </PrintContext.Provider>
  )
}

export { Provider }
